import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { message, Spin } from 'antd';
import { useNavigate } from 'react-router-dom';
import { PiClipboardTextBold } from 'react-icons/pi';
import { CollectFormAPIs, CollectForm_GetPage_RequestQuery } from '~/common/typescript/apis/CollectFormAPIs';
import type { CollectForm_Base } from '~/common/typescript/models/CollectForm';
import type { Project_Base } from '~/common/typescript/models/Project';
import { useProjects } from '~/screens/FreePhotoScreen/hooks/useProjects';
import Routes from '~/routes/routes';
import { UI_ScreenStyles } from '../UI_ScreenStyles';
import { createBreadcrumbItems, commonBreadcrumbs } from '~/utils/breadcrumbUtils';
import { CollectFormHeader } from './components/CollectFormHeader';
import { CollectFormFilters } from './components/CollectFormFilters';
import { CollectFormGrid } from './components/CollectFormGrid';
import { CollectFormList } from './components/CollectFormList';
import { CollectFormDetailDrawer } from './components/CollectFormDetailDrawer';
import { parseFormContent } from '~/components/Map/panels/GeocollectDatatablePanel/utils/CollectFormParser';

export const CollectFormScreen: React.FC = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [forms, setForms] = useState<CollectForm_Base[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState<number>(0);

  // Filter states
  const [selectedProjectId, setSelectedProjectId] = useState<number | 'all'>('all');
  const [searchText, setSearchText] = useState<string>('');
  const [versionFilter, setVersionFilter] = useState<'all' | 'v1' | 'v2'>('all');
  const [sortBy, setSortBy] = useState<'date_desc' | 'date_asc' | 'name_asc' | 'name_desc'>('date_desc');

  // Pagination states
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(12);

  // Detail drawer
  const [selectedFormForDetail, setSelectedFormForDetail] = useState<CollectForm_Base | null>(null);
  const [isDetailDrawerOpen, setIsDetailDrawerOpen] = useState<boolean>(false);

  // Projects list
  const { projects, loading: projectsLoading, isAdmin } = useProjects();

  const projectsMap = useMemo(() => {
    const map = new Map<number, Project_Base>();
    projects.forEach((p) => {
      map.set(p.id, p);
    });
    return map;
  }, [projects]);

  const fetchCollectForms = useCallback(async () => {
    setLoading(true);
    try {
      const request: CollectForm_GetPage_RequestQuery = {
        page: currentPage,
        pageSize: pageSize,
      };

      if (selectedProjectId !== 'all') {
        request.projectId = selectedProjectId;
      } else if (!isAdmin && projects.length > 0) {
        request.projectIds = projects.map((p) => p.id);
      }

      const response = await CollectFormAPIs.remote_CollectForm_getPage(request);

      setForms(response.results || []);
      setTotalCount(response.pagination?.total || (response.results || []).length);
    } catch (error) {
      console.error('[CollectFormScreen] Failed to fetch collect forms:', error);
      message.error('Không thể tải danh sách biểu mẫu');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, selectedProjectId, isAdmin, projects]);

  useEffect(() => {
    fetchCollectForms();
  }, [fetchCollectForms]);

  const handleDeleteForm = async (uuid: string) => {
    try {
      await CollectFormAPIs.remote_deleteCollectForm(uuid);
      message.success('Đã xoá biểu mẫu thành công');
      fetchCollectForms();
    } catch (error) {
      console.error('[CollectFormScreen] Failed to delete form:', error);
      message.error('Không thể xoá biểu mẫu');
    }
  };

  const handleViewDetail = (form: CollectForm_Base) => {
    setSelectedFormForDetail(form);
    setIsDetailDrawerOpen(true);
  };

  const handleCreateForm = () => {
    if (selectedProjectId !== 'all') {
      navigate(`${Routes.COLLECT_FORM_DESIGN}?projectId=${selectedProjectId}`);
    } else {
      navigate(Routes.COLLECT_FORM_DESIGN);
    }
  };

  // Client-side search and version filtering on loaded data
  const filteredForms = useMemo(() => {
    let result = [...forms];

    if (searchText.trim()) {
      const query = searchText.toLowerCase().trim();
      result = result.filter(
        (f) =>
          (f.name && f.name.toLowerCase().includes(query)) ||
          (f.description && f.description.toLowerCase().includes(query)),
      );
    }

    if (versionFilter !== 'all') {
      result = result.filter((f) => {
        const parsed = parseFormContent(f);
        const isV2 = f.form_version === 2 || parsed?.version === 'v2';
        return versionFilter === 'v2' ? isV2 : !isV2;
      });
    }

    result.sort((a, b) => {
      if (sortBy === 'date_asc') {
        return (a.created_at || '').localeCompare(b.created_at || '');
      }
      if (sortBy === 'name_asc') {
        return (a.name || '').localeCompare(b.name || '');
      }
      if (sortBy === 'name_desc') {
        return (b.name || '').localeCompare(a.name || '');
      }
      // date_desc
      return (b.created_at || '').localeCompare(a.created_at || '');
    });

    return result;
  }, [forms, searchText, versionFilter, sortBy]);

  const handlePageChange = (page: number, newPageSize: number) => {
    setCurrentPage(page);
    setPageSize(newPageSize);
  };

  return (
    <UI_ScreenStyles.UI_Wrapper>
      <UI_ScreenStyles.UI_Breadcrumb
        items={createBreadcrumbItems([
          commonBreadcrumbs.home,
          {
            title: 'Biểu mẫu',
            icon: <PiClipboardTextBold style={{ fontSize: '16px' }} />,
          },
        ])}
      />

      <UI_ScreenContainer>
        <CollectFormHeader
          viewMode={viewMode}
          totalCount={totalCount}
          onViewModeChange={setViewMode}
          onRefresh={fetchCollectForms}
          onCreateForm={handleCreateForm}
          loading={loading}
          isAdmin={isAdmin}
        />

        <CollectFormFilters
          searchText={searchText}
          onSearchChange={(val) => setSearchText(val)}
          selectedProjectId={selectedProjectId}
          onProjectChange={(val) => {
            setSelectedProjectId(val);
            setCurrentPage(1);
          }}
          projects={projects}
          projectsLoading={projectsLoading}
          versionFilter={versionFilter}
          onVersionFilterChange={(val) => setVersionFilter(val)}
          sortBy={sortBy}
          onSortByChange={setSortBy}
        />

        <UI_ContentArea>
          {loading && forms.length === 0 ? (
            <UI_LoadingWrapper>
              <Spin size="large" tip="Đang tải danh sách biểu mẫu..." />
            </UI_LoadingWrapper>
          ) : (
            <>
              {viewMode === 'grid' ? (
                <CollectFormGrid
                  forms={filteredForms}
                  projectsMap={projectsMap}
                  onViewDetail={handleViewDetail}
                  onDelete={handleDeleteForm}
                  currentPage={currentPage}
                  pageSize={pageSize}
                  totalCount={totalCount}
                  onPageChange={handlePageChange}
                />
              ) : (
                <CollectFormList
                  forms={filteredForms}
                  projectsMap={projectsMap}
                  loading={loading}
                  onViewDetail={handleViewDetail}
                  onDelete={handleDeleteForm}
                  currentPage={currentPage}
                  pageSize={pageSize}
                  totalCount={totalCount}
                  onPageChange={handlePageChange}
                />
              )}
            </>
          )}
        </UI_ContentArea>
      </UI_ScreenContainer>

      <CollectFormDetailDrawer
        visible={isDetailDrawerOpen}
        onClose={() => {
          setIsDetailDrawerOpen(false);
          setSelectedFormForDetail(null);
        }}
        form={selectedFormForDetail}
        projectsMap={projectsMap}
      />
    </UI_ScreenStyles.UI_Wrapper>
  );
};

export default CollectFormScreen;

const UI_ScreenContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: #ffffff;
  overflow-y: auto;
`;

const UI_ContentArea = styled.div`
  flex: 1;
`;

const UI_LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 360px;
`;
